import { Autocomplete, Box, paletteColors, Stack, TextField, Typography } from '@evaneos/b2b-ui';
import { SyntheticEvent, useMemo } from 'react';

import { useGetSelectAgents } from '../../../../resources/agent/hooks';
import { useGetAgentByDossierId } from '../../../../resources/dossier/queries';
import { useReassignAgentMutation } from '../../../../resources/dossier/mutations';
import { DossierId } from '../../../../resources/dossier/types';
import { Avatar } from '../../../../resources/agent/components/Avatar';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { useAuth } from '../../../../shared/providers/AuthProvider';

type AgentOption = {
    label: string;
    value: string;
    photoUrl: string | null;
};

type AgentSelectFormProps = {
    dossierId: DossierId;
    labelId: string;
    onClose: () => void;
};

export function AgentSelectForm({ dossierId, labelId, onClose }: Readonly<AgentSelectFormProps>) {
    const reassignMutation = useReassignAgentMutation(dossierId);

    const agents = useGetSelectAgents();
    const assignedAgent = useGetAgentByDossierId(dossierId);
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const { userId: loggedAgentId } = useAuth();

    const currentAgentValue = useMemo(() => {
        if (!assignedAgent) return undefined;
        return agents.find((agent) => agent.value === assignedAgent.id);
    }, [assignedAgent, agents]);

    const handleChange = (_event: SyntheticEvent, newValue: AgentOption | null) => {
        if (!newValue || !assignedAgent) return;

        if (newValue.value !== assignedAgent.id && !reassignMutation.isPending) {
            reassignMutation.mutate({ agentId: newValue.value });

            trackEvent((ampli, defaultProperties) => {
                ampli.agentAttributionManaged({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    is_agent_assigned: newValue.value === String(loggedAgentId),
                });
            });
        }

        onClose();
    };

    return (
        <form>
            <Autocomplete
                onChange={handleChange}
                autoHighlight
                autoComplete
                disablePortal
                options={agents}
                aria-labelledby={labelId}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                sx={{
                    '& .MuiInputBase-root': {
                        background: paletteColors.neutral[100],
                    },
                    '& .MuiInputBase-input': { fontSize: '1.4rem' },
                    '& .MuiInputLabel-root': { fontSize: '1.4rem' },
                }}
                slotProps={{
                    paper: { sx: { '& .MuiAutocomplete-option': { fontSize: '1.4rem' } } },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        autoFocus
                        sx={{ fontSize: '1.4rem' }}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                startAdornment: (
                                    <Box
                                        display="flex"
                                        component="span"
                                        alignItems="center"
                                        gap={1}
                                        paddingLeft={1}
                                    >
                                        <Avatar
                                            name={currentAgentValue?.label ?? ''}
                                            photoUrl={currentAgentValue?.photoUrl}
                                            component="span"
                                            aria-hidden="true"
                                            sx={{
                                                fontSize: '0.8rem',
                                                height: 16,
                                                width: 16,
                                                minHeight: 16,
                                                minWidth: 16,
                                            }}
                                        />
                                        <Typography variant="body2" component="span">
                                            {currentAgentValue?.label ?? ''}
                                        </Typography>
                                    </Box>
                                ),
                            },
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <Stack
                        component="li"
                        height="40px"
                        direction="row"
                        {...props}
                        key={option.value}
                    >
                        <Box display="flex" component="span" alignItems="center" gap={1}>
                            <Avatar
                                name={option.label}
                                photoUrl={option.photoUrl}
                                component="span"
                                aria-hidden="true"
                                sx={{
                                    fontSize: '0.8rem',
                                    height: 16,
                                    width: 16,
                                    minHeight: 16,
                                    minWidth: 16,
                                }}
                            />
                            <Typography variant="body2" component="span">
                                {option.label}
                            </Typography>
                        </Box>
                    </Stack>
                )}
                disableClearable
                size="small"
                open
                popupIcon={null}
            />
        </form>
    );
}
